import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CreditCard,
  Check,
  Sparkles,
  Loader2,
  AlertCircle,
  Calendar,
  Receipt,
} from 'lucide-react';
import { billingAPI } from '../api/client';

const PLAN_GRADIENTS = {
  starter: 'from-sky-500 to-blue-600',
  pro: 'from-violet-500 to-fuchsia-600',
  business: 'from-amber-500 to-orange-600',
};

const STATUS_COLORS = {
  trial: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  past_due: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
  canceled: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
};

export default function Billing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [plansData, setPlansData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [billingMode, setBillingMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoadingId, setCheckoutLoadingId] = useState(null);
  const [pollMessage, setPollMessage] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [plans, sub, txns, mode] = await Promise.all([
        billingAPI.getPlans(),
        billingAPI.getSubscription(),
        billingAPI.listTransactions(),
        billingAPI.getMode(),
      ]);
      setPlansData(plans);
      setSubscription(sub);
      setTransactions(txns);
      setBillingMode(mode);
    } catch (e) {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    load();
  }, []);

  // Handle return-from-Stripe (poll status)
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');
    if (canceled) {
      toast.info('Checkout canceled');
      const np = new URLSearchParams(searchParams);
      np.delete('canceled');
      setSearchParams(np, { replace: true });
      return;
    }
    if (!sessionId) return;

    let attempts = 0;
    const maxAttempts = 8;
    let cancelled = false;

    setPollMessage('Confirming payment...');
    const poll = async () => {
      if (cancelled) return;
      if (attempts >= maxAttempts) {
        setPollMessage(null);
        toast.error('Payment status check timed out. Refresh to retry.');
        return;
      }
      attempts += 1;
      try {
        const status = await billingAPI.getCheckoutStatus(sessionId);
        if (status.payment_status === 'paid') {
          setPollMessage(null);
          toast.success(
            status.already_processed
              ? 'Subscription already active.'
              : `Payment successful — ${status.plan?.toUpperCase()} plan activated!`,
          );
          const np = new URLSearchParams(searchParams);
          np.delete('session_id');
          setSearchParams(np, { replace: true });
          load();
          return;
        }
        if (status.status === 'expired') {
          setPollMessage(null);
          toast.error('Checkout session expired.');
          return;
        }
        setPollMessage('Payment is being processed...');
        setTimeout(poll, 2000);
      } catch (e) {
        setPollMessage(null);
        toast.error('Could not verify payment status.');
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('session_id'), searchParams.get('canceled')]);

  const handleCheckout = async (packageId) => {
    setCheckoutLoadingId(packageId);
    try {
      const { url } = await billingAPI.createCheckout(packageId, window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error('Could not start checkout');
      setCheckoutLoadingId(null);
    }
  };

  const handleSubscribeDemo = async (packageId) => {
    setCheckoutLoadingId(packageId);
    try {
      const result = await billingAPI.subscribeDemo(packageId);
      toast.success(`${result.plan.toUpperCase()} plan activated (DEMO)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not activate plan');
    } finally {
      setCheckoutLoadingId(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription and revert to free trial?')) return;
    try {
      await billingAPI.cancel();
      toast.success('Subscription canceled');
      await load();
    } catch (e) {
      toast.error('Could not cancel');
    }
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sky-400" />
      </div>
    );
  }

  const statusKey = subscription?.status || 'trial';

  return (
    <div className="space-y-8 p-6" data-testid="billing-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <CreditCard className="h-8 w-8 text-sky-400" /> Billing & Subscription
          </h1>
          <p className="text-slate-400 mt-1">
            Manage your plan, payment methods, and invoices.
          </p>
        </div>
        <div
          className={`px-4 py-2 rounded-xl border text-sm font-medium ${STATUS_COLORS[statusKey] || STATUS_COLORS.trial}`}
          data-testid="subscription-status-badge"
        >
          {statusKey.replace('_', ' ').toUpperCase()}
          {subscription?.trial_days_left != null && statusKey === 'trial' && (
            <span className="ml-2 opacity-80">
              · {subscription.trial_days_left} days left
            </span>
          )}
        </div>
      </div>

      {pollMessage && (
        <div
          className="flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-300"
          data-testid="payment-poll-message"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          {pollMessage}
        </div>
      )}

      {billingMode?.mode === 'demo' && (
        <div
          className="flex items-start gap-3 rounded-xl border border-violet-500/30 bg-violet-500/10 px-4 py-3 text-violet-200"
          data-testid="billing-demo-banner"
        >
          <Sparkles className="h-5 w-5 text-violet-400 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-violet-300">Recurring Billing — DEMO mode</div>
            <div className="opacity-80">
              {billingMode.demo_notice}
            </div>
            <div className="opacity-70 text-xs mt-1">
              Use <b>Subscribe (Demo)</b> to simulate activation, or <b>Upgrade</b> to run a real one-time Stripe Checkout in test mode.
            </div>
          </div>
        </div>
      )}

      {/* Current subscription card */}
      <div
        className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6"
        data-testid="current-subscription-card"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Stat label="Company" value={subscription?.company_name || '-'} />
          <Stat
            label="Plan"
            value={(subscription?.plan || 'free_trial').replace('_', ' ').toUpperCase()}
            icon={<Sparkles className="h-4 w-4 text-violet-400" />}
          />
          <Stat
            label={statusKey === 'active' ? 'Renews' : 'Trial Ends'}
            value={
              (subscription?.current_period_end || subscription?.trial_ends_at || '').slice(0, 10) ||
              '-'
            }
            icon={<Calendar className="h-4 w-4 text-sky-400" />}
          />
        </div>
      </div>

      {/* Plans */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4">Choose Your Plan</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plansData?.packages?.map((pkg) => {
            const isCurrent = plansData?.current_plan === pkg.plan && statusKey === 'active';
            return (
              <div
                key={pkg.id}
                data-testid={`plan-card-${pkg.id}`}
                className={`relative rounded-2xl border bg-slate-900/60 p-6 flex flex-col ${
                  isCurrent ? 'border-emerald-500/50 shadow-emerald-500/10 shadow-xl' : 'border-slate-800'
                }`}
              >
                {isCurrent && (
                  <div className="absolute -top-3 right-4 bg-emerald-500 text-slate-950 text-xs font-bold px-3 py-1 rounded-full">
                    CURRENT
                  </div>
                )}
                <div
                  className={`inline-block mb-3 text-xs font-semibold uppercase tracking-wider bg-gradient-to-r ${PLAN_GRADIENTS[pkg.id]} text-white px-3 py-1 rounded-full w-fit`}
                >
                  {pkg.name}
                </div>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-4xl font-bold text-white">${pkg.amount}</span>
                  <span className="text-slate-400">/mo</span>
                </div>
                <ul className="space-y-2 text-sm text-slate-300 flex-1 mb-6">
                  {pkg.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`upgrade-button-${pkg.id}`}
                  disabled={checkoutLoadingId === pkg.id || isCurrent}
                  onClick={() => handleCheckout(pkg.id)}
                  className={`w-full py-3 rounded-xl font-semibold transition bg-gradient-to-r ${PLAN_GRADIENTS[pkg.id]} text-white hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                >
                  {checkoutLoadingId === pkg.id ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Redirecting...
                    </>
                  ) : isCurrent ? (
                    'Current Plan'
                  ) : (
                    `Upgrade to ${pkg.name}`
                  )}
                </button>
                {billingMode?.mode === 'demo' && !isCurrent && (
                  <button
                    data-testid={`subscribe-demo-button-${pkg.id}`}
                    disabled={checkoutLoadingId === pkg.id}
                    onClick={() => handleSubscribeDemo(pkg.id)}
                    className="mt-2 w-full py-2.5 rounded-xl text-sm font-semibold border border-violet-500/40 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Sparkles className="h-4 w-4" /> Subscribe (Demo)
                  </button>
                )}
                {isCurrent && (
                  <button
                    data-testid={`cancel-button-${pkg.id}`}
                    onClick={handleCancel}
                    className="mt-2 w-full py-2.5 rounded-xl text-sm font-semibold border border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    Cancel subscription
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Transactions */}
      <div
        className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6"
        data-testid="transactions-card"
      >
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Receipt className="h-5 w-5 text-sky-400" /> Recent Transactions
        </h2>
        {transactions.length === 0 ? (
          <div className="text-slate-400 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> No transactions yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-slate-400 border-b border-slate-800">
                <tr>
                  <th className="text-left py-2">Date</th>
                  <th className="text-left">Plan</th>
                  <th className="text-left">Amount</th>
                  <th className="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id} className="border-b border-slate-800/60" data-testid={`txn-${t.id}`}>
                    <td className="py-2 text-slate-300">{(t.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="text-slate-300 uppercase">{t.plan}</td>
                    <td className="text-slate-300">
                      ${t.amount} {(t.currency || 'usd').toUpperCase()}
                    </td>
                    <td>
                      <span
                        className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                          t.payment_status === 'paid'
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : t.payment_status === 'pending'
                              ? 'bg-amber-500/10 text-amber-400'
                              : 'bg-slate-500/10 text-slate-400'
                        }`}
                      >
                        {t.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon }) => (
  <div>
    <div className="text-slate-400 text-xs uppercase tracking-wider mb-1 flex items-center gap-1">
      {icon} {label}
    </div>
    <div className="text-white font-semibold">{value}</div>
  </div>
);
